// One note row (mock s26 .nt-item): author avatar, text (or inline editor),
// optional voice-note player, meta line, and quick actions. Pinned notes get
// the warm highlighted style; teammates' org-shared notes are strictly
// read-only (no actions) with author attribution — the legacy permission
// model from useSellerNotes/QuickNotesInline.
//
// A SYSTEM comment (`note.system`: an automated write's record of what it
// created or changed — the lead form, a webhook, Daniela, a bot, a call, a
// signature) is written by the server into the realtor's own notes. It is
// shown as one: the source's icon instead of the realtor's initials, its
// first line ("עודכן אוטומטית · טופס לידים") as a label over the changes, no
// author, and no edit — it is a record; pin, share and delete stay theirs.
import { useCallback, useRef, useState } from "react";
import { Box, IconButton, InputBase, Stack, Tooltip, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import {
  AssignmentIndOutlined,
  AutoAwesomeOutlined,
  DeleteOutline,
  Edit,
  Group,
  HistoryEdu,
  LinkOutlined,
  PhoneInTalkOutlined,
  PushPin,
  PushPinOutlined,
  SmartToyOutlined
} from "@material-ui/icons";
import Label from "components/Label";
import { formatNoteDate, initialsOf } from "./notesData";
import type { DisplayUserNote } from "hooks/useListingNotes";
import type { ContactSystemNoteSource } from "../../../../../../@types/sharedSchema";

/** Who wrote a system comment, as an icon; a source this build does not know yet gets the sparkle. */
const SYSTEM_NOTE_ICONS: Record<ContactSystemNoteSource, typeof AutoAwesomeOutlined> = {
  form: AssignmentIndOutlined,
  webhook: LinkOutlined,
  ai: AutoAwesomeOutlined,
  bot: SmartToyOutlined,
  call: PhoneInTalkOutlined,
  signature: HistoryEdu
};

type ContactNoteRowProps = {
  note: DisplayUserNote;
  /** Display name for own notes (teammates' resolve from organizationUsers). */
  myName: string;
  /** Whether the user is in an org (gates the share toggle). */
  canShare: boolean;
  onTogglePin: (note: DisplayUserNote) => void;
  onToggleShare: (note: DisplayUserNote) => void;
  /** Rejects on save failure (the tab shows the snackbar). */
  onSaveEdit: (note: DisplayUserNote, text: string) => Promise<void>;
  onDelete: (note: DisplayUserNote) => void;
};

function QuickActionButton({
  title,
  onClick,
  active = false,
  children
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  children: JSX.Element;
}): JSX.Element {
  const theme = useTheme();
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={title}
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
          color: active ? "warning.dark" : "text.secondary"
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}

export function ContactNoteRow({
  note,
  myName,
  canShare,
  onTogglePin,
  onToggleShare,
  onSaveEdit,
  onDelete
}: ContactNoteRowProps): JSX.Element {
  const theme = useTheme();
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  // Enter-commit is followed by a native blur from the unmounting editor —
  // this ref keeps the commit from firing twice for one edit session.
  const committedRef = useRef(false);

  const isPinned = note.pinned === true;
  const isMine = note.isMine !== false;
  // A note written on the contact's PROPERTY (`usePropertyNotes`): shown here, edited on the
  // property — the actions below write the CONTACT's notes document and would not find it.
  const propertyNote = note.listing;
  const canAct = isMine && !propertyNote;
  const isShared = (note.organizationIds?.length ?? 0) > 0;
  const isVoice = !!note.audioUrl;
  const authorName = isMine ? myName : note.authorName ?? note.authorEmail;
  const system = note.system;
  const SystemIcon = system ? SYSTEM_NOTE_ICONS[system.source] ?? AutoAwesomeOutlined : null;
  const [systemHead = "", ...systemLines] = system ? (note.note ?? "").split("\n") : [];

  const startEdit = useCallback(() => {
    committedRef.current = false;
    setEditText(note.note);
    setEditing(true);
  }, [note.note]);

  const commitEdit = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);
    const trimmed = editText.trim();
    // Empty result: keep the original (delete has its own explicit action).
    if (!trimmed || trimmed === note.note) return;
    void onSaveEdit(note, trimmed).catch(() => {
      // Save failed (the tab already showed the snackbar) — reopen the editor
      // with the attempted text so the user's edit isn't lost, and keep the
      // rejection from surfacing as an unhandled promise rejection.
      committedRef.current = false;
      setEditText(trimmed);
      setEditing(true);
    });
  }, [editText, note, onSaveEdit]);

  // Same rule as the composer: `dir="auto"` has nothing to resolve while the
  // editor is empty (a voice note with no transcription) and falls back to LTR,
  // parking the caret on the wrong side in this RTL UI. Anchor empty to the UI
  // direction; `auto` takes over as soon as there is text, so an LTR value
  // still renders LTR.
  const editDir = editText.trim() ? "auto" : theme.direction;

  // A system comment has no author to name: its label already says where it came from.
  const metaParts: string[] = [system ? "" : authorName, formatNoteDate(note.noteTime)].filter(Boolean);
  if (isPinned) metaParts.push("נעוצה — מוצגת גם בסקירה");

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap={1.5}
      sx={{
        py: 2,
        ...(isPinned
          ? {
              px: 2,
              mb: 1,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.warning.main, 0.06),
              border: `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`
            }
          : {
              borderBottom: `1px dashed ${theme.palette.divider}`,
              "&:last-of-type": { borderBottom: "none" }
            })
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flex: "none",
          bgcolor: SystemIcon
            ? alpha(theme.palette.grey[500], 0.16)
            : isMine
            ? alpha(theme.palette.info.main, 0.16)
            : alpha(theme.palette.secondary.main, 0.16),
          color: SystemIcon ? "text.secondary" : isMine ? theme.palette.info.dark : theme.palette.secondary.dark
        }}
        aria-hidden
      >
        {SystemIcon ? <SystemIcon sx={{ fontSize: 18 }} /> : initialsOf(authorName)}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {editing ? (
          <InputBase
            fullWidth
            multiline
            maxRows={6}
            autoFocus
            value={editText}
            onChange={(event) => setEditText(event.target.value)}
            onBlur={commitEdit}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                commitEdit();
              }
              if (event.key === "Escape") {
                setEditText(note.note);
                setEditing(false);
              }
            }}
            inputProps={{ dir: editDir, "aria-label": "עריכת הערה" }}
            sx={{
              fontSize: 13.5,
              lineHeight: 1.6,
              p: 0.75,
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
              bgcolor: "background.paper"
            }}
          />
        ) : system ? (
          <>
            {/* The house Label, allowed to WRAP: it is a sentence, and on a phone the text column
                is ~150px, so a one-line pill ran over the action buttons. */}
            <Label
              color="default"
              sx={{
                mb: systemLines.length ? 0.75 : 0,
                maxWidth: "100%",
                height: "auto",
                minHeight: 22,
                py: 0.25,
                lineHeight: 1.5,
                whiteSpace: "normal",
                textAlign: "start"
              }}
            >
              {systemHead}
            </Label>
            {systemLines.length > 0 && (
              <Typography variant="body2" dir="auto" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>
                {systemLines.join("\n")}
              </Typography>
            )}
          </>
        ) : (
          <Typography
            variant="body2"
            dir="auto"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.6,
              ...(isVoice && !note.note ? { color: "text.secondary", fontStyle: "italic" } : {})
            }}
          >
            {isPinned ? "📌 " : ""}
            {note.note || (isVoice ? "הערה קולית (ללא תמלול)" : "")}
          </Typography>
        )}

        {isVoice && (
          /* eslint-disable-next-line jsx-a11y/media-has-caption */
          <audio
            controls
            preload="none"
            src={note.audioUrl}
            style={{ display: "block", width: "100%", maxWidth: 340, height: 36, marginTop: 8 }}
          />
        )}

        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          flexWrap="wrap"
          sx={{ mt: 0.5 }}
        >
          {propertyNote && (
            <Tooltip title="נכתבה על הנכס בלוח הנכסים או בכרטיס הנכס, ושם עורכים אותה">
              {/* A span for the tooltip to hold: `Label` takes no ref. The label may wrap, like
                  the system label above: an address is long, and a phone's column is narrow. */}
              <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%" }}>
                <Label
                  color="warning"
                  sx={{ maxWidth: "100%", height: "auto", minHeight: 22, py: 0.25, whiteSpace: "normal", textAlign: "start" }}
                >
                  {propertyNote.label ? `על הנכס · ${propertyNote.label}` : "על הנכס"}
                </Label>
              </Box>
            </Tooltip>
          )}
          {!isMine && <Label color="info">משרד</Label>}
          {isMine && isShared && <Label color="primary">משותפת עם המשרד</Label>}
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {metaParts.join(" · ")}
          </Typography>
        </Stack>
      </Box>

      {canAct && (
        <Stack direction="row" gap={0.75} sx={{ flex: "none", pt: 0.25 }}>
          <QuickActionButton
            title={isPinned ? "ביטול נעיצה" : "נעיצה"}
            active={isPinned}
            onClick={() => onTogglePin(note)}
          >
            {isPinned ? <PushPin sx={{ fontSize: 15 }} /> : <PushPinOutlined sx={{ fontSize: 15 }} />}
          </QuickActionButton>
          {canShare && (
            <QuickActionButton
              title={isShared ? "הפיכה לפרטית — תוסתר מהמשרד" : "שיתוף ההערה עם המשרד"}
              onClick={() => onToggleShare(note)}
            >
              <Group
                sx={{
                  fontSize: 15,
                  color: isShared ? theme.palette.primary.main : undefined
                }}
              />
            </QuickActionButton>
          )}
          {!editing && !system && (
            <QuickActionButton title="עריכה" onClick={startEdit}>
              <Edit sx={{ fontSize: 15 }} />
            </QuickActionButton>
          )}
          <QuickActionButton title="מחיקה" onClick={() => onDelete(note)}>
            <DeleteOutline sx={{ fontSize: 15 }} />
          </QuickActionButton>
        </Stack>
      )}
    </Stack>
  );
}
